import React from 'react'
import { Sparkles, TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react'
import type { TradeIntelligenceResult } from '../../lib/trade-intelligence'
import { cn } from '../../lib/utils'

interface TinyTradeSignalsProps {
    signal: TradeIntelligenceResult | null
}

export const TinyTradeSignals: React.FC<TinyTradeSignalsProps> = ({ signal }) => {
    if (!signal) {
        return (
            <div className="w-full bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 border border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center gap-2 text-slate-400">
                <Activity size={16} className="animate-pulse" />
                <span className="text-xs font-semibold uppercase tracking-wider">Awaiting Quantum Signal Data...</span>
            </div>
        )
    }

    const isBull = signal.signalType === 'BULLISH'
    const isBear = signal.signalType === 'BEARISH'
    const isNeutral = signal.signalType === 'NEUTRAL'

    const colorClasses = isBull
        ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
        : isBear
          ? 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800'
          : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-400 border border-slate-200 dark:border-slate-700'

    const Icon = isBull ? TrendingUp : isBear ? TrendingDown : Minus

    return (
        <div
            className={cn(
                'w-full rounded-2xl p-4 shadow-sm transition-colors flex items-center justify-between flex-wrap gap-4 relative overflow-hidden',
                colorClasses,
            )}
        >
            {/* Background subtle decoration */}
            <div className="absolute right-0 top-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none transform translate-x-1/4 -translate-y-1/4">
                <Sparkles size={120} />
            </div>

            {/* Left side: Icon, Type, Subtype */}
            <div className="flex items-center gap-3 relative z-10">
                <div
                    className={cn(
                        'p-2 rounded-xl flex items-center justify-center',
                        isBull
                            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400'
                            : isBear
                              ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400'
                              : 'bg-slate-200 dark:bg-slate-700 text-slate-500',
                    )}
                >
                    <Icon size={18} strokeWidth={3} />
                </div>
                <div>
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[9px] font-black uppercase tracking-widest opacity-60 flex items-center gap-1">
                            <Sparkles size={10} /> TRADE INTEL • QUANTUM SIGNAL
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-black uppercase tracking-wider text-sm flex items-center gap-1.5">
                            {signal.signalType}
                        </span>
                        <span className="text-xs opacity-60 font-bold px-1.5 py-0.5 rounded backdrop-blur-sm bg-black/5 dark:bg-white/10">
                            {signal.signalSubType}
                        </span>
                    </div>
                    {signal.setup && (
                        <p className="text-xs mt-1 font-medium opacity-80 max-w-lg truncate">{signal.setup.header}</p>
                    )}
                </div>
            </div>

            {/* Right side: Score & Win Rate */}
            <div className="flex flex-col items-end gap-1 relative z-10">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest opacity-70">
                    <span>Power: {signal.signalStrength}</span>
                </div>
                <div className="flex items-end gap-1.5 bg-white/50 dark:bg-black/20 px-3 py-1.5 rounded-lg border border-white/20 dark:border-black/20">
                    <span className="text-xs font-bold uppercase tracking-widest opacity-70 pb-0.5">Win Prob</span>
                    <span className="text-lg font-black font-mono leading-none">{signal.conviction}%</span>
                </div>
            </div>
        </div>
    )
}
